// React & React hooks
import { useRef, useCallback, useMemo } from 'react'

// Redux Toolkit
import { useSelector, shallowEqual, useDispatch } from 'react-redux'

// Store & API
import { timesheetApi } from '../../store/api/timesheet/timesheetApi'
import { TimesheetStoreState } from '../../store/timesheetStore'
import {
	resetProgress,
	useApproveTimesheetBatchMutation,
	selectMassApprovalProgress,
	setData,
} from '../../store/slices/timesheet/massApprovalSlice'

// Utils & Hooks
import { useGetTimesheetsFilteredQuery } from '../../utils/hooks/useGetTimesheetsFilteredQuery'
import { useSignalR } from '../../utils/hooks/signalR'
import { devError, devLog, devWarn } from '../../utils/feedback'

// Modal system
import { useModal } from '../../store/ModalProvider'
import { modalDataType } from '../../types/modal'

// Types, Interfaces & Enums
import { MassApprovalAPIResponse } from '../../types/massApproval'
import { TimesheetStatusEnum } from '../../store/slices/timesheet/timesheetDetailSlice'
import { TimesheetResponse } from '../../types/timesheetsAPI'

// UI Components
import CustomButton from '../v2/general/CustomButton/CustomButton'

const SubmittedWithoutChanges: React.FC = () => {
	const dispatch = useDispatch()
	const submittedWithoutChanges = useSelector(
		(state: TimesheetStoreState) =>
			Array.isArray(state.timesheetDetails?.filters.status)
				? state.timesheetDetails.filters.status.includes(TimesheetStatusEnum.SubmittedWithoutChanges) &&
					state.timesheetDetails.filters.status.length === 1
				: state.timesheetDetails?.filters.status === TimesheetStatusEnum.SubmittedWithoutChanges,
		shallowEqual,
	)

	// Access mass approval progress
	const massApprovalProgress = useSelector(selectMassApprovalProgress)

	const { data: timesheets } = useGetTimesheetsFilteredQuery()

	const { openModal, closeModal } = useModal()

	const [approveTimesheetBatch, { isLoading }] = useApproveTimesheetBatchMutation()

	// Normalize items before sending to the API / modal (prevents `comments: null`)
	const sanitizedItemList = useMemo(() => {
		const list = (timesheets?.itemList ?? []) as TimesheetResponse[]
		return list.map(item => ({
			...item,
			comments: item?.comments ?? '',
			status: TimesheetStatusEnum.SubmittedWithoutChanges as const,
		}))
	}, [timesheets?.itemList])

	// Memoize SignalR options to prevent recreation
	const signalROptions = useMemo(
		() => ({
			autoStart: false,
			withCredentials: false,
			retryAttempts: 2,
			fallbackUrls: [
				`${process.env.REACT_APP_API_BASE_URL}/hubs/progress`,
				`${process.env.REACT_APP_API_BASE_URL}/api/progress-hub`,
			],
		}),
		[],
	)

	const { startConnection, stopConnection, connectionStatus } = useSignalR(
		`${process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '')}/progressHub`,
		[],
		signalROptions,
	)

	// Ref to track event listener registration
	const eventListenerRegisteredRef = useRef(false)

	const submitTimesheetsForMassApproval = useCallback(async () => {
		closeModal()

		try {
			// Reset progress before starting with error handling for browser extension issues
			try {
				dispatch(resetProgress())
			} catch (dispatchError) {
				if (
					typeof dispatchError === 'object' &&
					dispatchError !== null &&
					'message' in dispatchError &&
					typeof dispatchError.message === 'string' &&
					(dispatchError.message.includes('disconnected port') || dispatchError.message.includes('port object'))
				) {
					devWarn('[SubmittedWithoutChanges] - Browser extension communication error, continuing...')
				} else {
					throw dispatchError
				}
			}

			// Try to start SignalR connection (optional for progress updates)
			try {
				await startConnection()
				devLog('[SubmittedWithoutChanges] - SignalR connection started', connectionStatus)
			} catch (startError) {
				devWarn(
					'[SubmittedWithoutChanges] - SignalR connection failed, continuing without real-time updates:',
					startError,
				)
				// Continue without SignalR - it's not critical for the operation
			}

			// Trigger the approval mutation
			const result = (await approveTimesheetBatch(sanitizedItemList).unwrap()) as MassApprovalAPIResponse

			devLog('[SubmittedWithoutChanges] - Mass approval result:', result)
			if (result) {
				dispatch(setData(result))
			}

			// Show success feedback to user
			// You might want to show a toast notification here
		} catch (error) {
			devError('[SubmittedWithoutChanges] - Failed to approve timesheets:', error)

			// Handle specific error types
			if (error && typeof error === 'object' && 'status' in error) {
				const statusError = error as { status: number; data?: unknown }
				if (statusError.status === 500) {
					devError('[SubmittedWithoutChanges] - Server error (500) occurred')
					// You might want to show a user-friendly error message here
				}
			}

			// Don't re-throw the error to prevent unhandled promise rejection
		} finally {
			// Clean up SignalR connection if it was started
			try {
				await stopConnection()
				devLog('[SubmittedWithoutChanges] - SignalR connection stopped')
			} catch (stopError) {
				devWarn('[SubmittedWithoutChanges] - Error stopping SignalR connection:', stopError)
			}
			eventListenerRegisteredRef.current = false

			// Revalidate timesheet totals tag to refresh data
			await new Promise(resolve => setTimeout(resolve, 500)) // Slight delay to ensure backend processing
			dispatch(timesheetApi.util.invalidateTags([{ type: 'TimesheetTotals' }]))
		}
	}, [closeModal, dispatch, startConnection, stopConnection, approveTimesheetBatch, sanitizedItemList])

	if (!submittedWithoutChanges) return null

	return (
		<CustomButton
			aria-label='mass-approval-btn'
			sx={{
				margin: 'auto auto 0 auto',
				'&.Mui-disabled': {
					width: '283.293px',
					padding: '6px 16px',
					borderRadius: '4px',
				},
				'& .MuiCircularProgress-root': {
					width: '24px !important',
					height: '24px !important',
				},
			}}
			disabled={isLoading && massApprovalProgress.progress < 100}
			isLoading={isLoading}
			variant='contained'
			color='secondary'
			onClick={() =>
				openModal('Aprovar Folhas de Ponto sem alterações', sanitizedItemList as unknown as modalDataType, [
					submitTimesheetsForMassApproval,
				])
			}
		>
			{`APROVAR FOLHAS SEM ALTERAÇÕES (${timesheets?.totalResults})`}
		</CustomButton>
	)
}

export default SubmittedWithoutChanges
